import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  Lock, Eye, Users, MousePointerClick, Globe, Monitor, Smartphone, Tablet,
  RefreshCw, Activity, LayoutDashboard, FileText, Radio, Zap, TrendingUp, ArrowUpRight, ArrowDownRight, Clock, User,
} from 'lucide-react';
import '../styles/admin-analytics.css';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#EC4899'];
const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
const RANGES = [
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
];

const NAV_ITEMS = [
  { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
  { id: 'funnels', icon: TrendingUp, label: 'Funnels' },
  { id: 'pages', icon: FileText, label: 'Pages' },
  { id: 'realtime', icon: Radio, label: 'Realtime' },
  { id: 'events', icon: Zap, label: 'Events' },
];

/* Custom tooltip */
function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="aa-tooltip">
      <div className="aa-tooltip__label">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="aa-tooltip__value">
          {formatter ? formatter(p.value) : p.value}
        </div>
      ))}
    </div>
  );
}

/* Password Gate */
function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw.trim()) { setError(false); onUnlock(pw.trim()); }
    else { setError(true); }
  };
  return (
    <div className="aa-gate" data-testid="analytics-password-gate">
      <div className="aa-gate__orbs">
        <div className="aa-gate__orb aa-gate__orb--1" />
        <div className="aa-gate__orb aa-gate__orb--2" />
        <div className="aa-gate__orb aa-gate__orb--3" />
      </div>
      <div className="aa-gate__card">
        <div className="aa-gate__lockring">
          <Lock size={22} strokeWidth={1.5} />
        </div>
        <h2>Skye Analytics</h2>
        <p>Enter your admin password</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password" value={pw} onChange={e => setPw(e.target.value)}
            placeholder="••••••••" className={error ? 'aa-gate__input--error' : ''}
            autoFocus data-testid="analytics-password-input"
          />
          <button type="submit" data-testid="analytics-password-submit">
            Unlock Dashboard <ArrowUpRight size={15} />
          </button>
        </form>
      </div>
    </div>
  );
}

/* Stat Card */
function StatCard({ icon: Icon, label, value, color, idx }) {
  return (
    <div className="aa-stat" style={{ '--stat-color': color, animationDelay: `${0.08 * idx}s` }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="aa-stat__top">
        <div className="aa-stat__icon"><Icon size={16} strokeWidth={2} /></div>
        <span className="aa-stat__label">{label}</span>
      </div>
      <span className="aa-stat__value">{value}</span>
    </div>
  );
}

/* ============================
   VIEW: Overview
   ============================ */
function OverviewView({ data, range }) {
  const { summary, views_over_time, top_pages, devices, referrers } = data;
  return (
    <>
      {/* Stats */}
      <div className="aa-stats" data-testid="analytics-summary">
        <StatCard icon={Eye} label="Page Views" value={summary.total_page_views.toLocaleString()} color="#8B5CF6" idx={0} />
        <StatCard icon={Users} label="Visitors" value={summary.unique_visitors.toLocaleString()} color="#3B82F6" idx={1} />
        <StatCard icon={Activity} label="Sessions" value={summary.unique_sessions.toLocaleString()} color="#10B981" idx={2} />
        <StatCard icon={MousePointerClick} label="Events" value={summary.total_events.toLocaleString()} color="#F59E0B" idx={3} />
      </div>

      {/* Traffic chart */}
      <div className="aa-card aa-card--full" data-testid="traffic-chart" style={{ animationDelay: '0.2s' }}>
        <div className="aa-card__head">
          <h3>Traffic</h3>
          <span className="aa-card__badge">{summary.total_page_views} total</span>
        </div>
        <ResponsiveContainer width="100%" height={230}>
          <AreaChart data={views_over_time}>
            <defs>
              <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.03)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: '#52525B', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => range === '24h' ? v.slice(11) + ':00' : v.slice(5)} />
            <YAxis tick={{ fill: '#52525B', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
            <Tooltip content={<ChartTooltip formatter={v => `${v} views`} />} />
            <Area type="monotone" dataKey="views" stroke="#8B5CF6" fill="url(#aGrad)" strokeWidth={2} dot={false} activeDot={{ r: 5, fill: '#8B5CF6', stroke: '#0a0d16', strokeWidth: 3 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom row: top 5 pages + devices + sources */}
      <div className="aa-row-3" style={{ animationDelay: '0.3s' }}>
        <div className="aa-card" data-testid="top-pages">
          <h3>Top Pages</h3>
          <div className="aa-list">
            {top_pages.slice(0, 5).map((p, i) => (
              <div key={i} className="aa-list__item">
                <span className="aa-list__rank">{i + 1}</span>
                <span className="aa-list__name">{p.page}</span>
                <div className="aa-list__bar"><div style={{ width: `${(p.views / (top_pages[0]?.views || 1)) * 100}%` }} /></div>
                <span className="aa-list__val">{p.views}</span>
              </div>
            ))}
            {top_pages.length === 0 && <p className="aa-empty">No data yet</p>}
          </div>
        </div>

        <div className="aa-card" data-testid="devices-chart">
          <h3>Devices</h3>
          {devices.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={devices} cx="50%" cy="50%" innerRadius={48} outerRadius={66} dataKey="value" paddingAngle={4} stroke="none">
                    {devices.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="aa-legend">
                {devices.map((d, i) => {
                  const Ic = DEVICE_ICONS[d.name] || Monitor;
                  return <span key={i} className="aa-legend__item"><Ic size={11} style={{ color: COLORS[i % COLORS.length] }} /> {d.name} <strong>{d.value}</strong></span>;
                })}
              </div>
            </>
          ) : <p className="aa-empty">No data</p>}
        </div>

        <div className="aa-card" data-testid="referrers">
          <h3>Sources</h3>
          <div className="aa-list">
            {referrers.map((r, i) => (
              <div key={i} className="aa-list__item">
                <Globe size={13} className="aa-list__icon" />
                <span className="aa-list__name">{r.source}</span>
                <span className="aa-list__val">{r.visits}</span>
              </div>
            ))}
            {referrers.length === 0 && <p className="aa-empty">No data yet</p>}
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================
   VIEW: Funnels (demos + audits)
   ============================ */
function FunnelsView({ password, range }) {
  const [funnelData, setFunnelData] = useState(null);

  useEffect(() => {
    const fetchFunnels = async () => {
      try {
        const res = await fetch(`${API}/api/analytics/funnels?password=${encodeURIComponent(password)}&range=${range}`);
        if (res.ok) setFunnelData(await res.json());
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Funnels fetch failed:', err);
      }
    };
    fetchFunnels();
    const iv = setInterval(fetchFunnels, 30000);
    return () => clearInterval(iv);
  }, [password, range]);

  if (!funnelData) return <p className="aa-empty">Loading funnels...</p>;

  const { demo, audit, recent } = funnelData;

  return (
    <>
      {/* Funnel summary cards */}
      <div className="aa-funnel-row">
        <div className="aa-funnel-card" data-testid="funnel-demo">
          <div className="aa-funnel-card__header">
            <MousePointerClick size={18} />
            <span>Chat Demos</span>
          </div>
          <div className="aa-funnel-card__stats">
            <div className="aa-funnel-metric">
              <span className="aa-funnel-metric__val">{demo.started}</span>
              <span className="aa-funnel-metric__label">Started</span>
            </div>
            <div className="aa-funnel-arrow">
              <ArrowUpRight size={16} />
            </div>
            <div className="aa-funnel-metric">
              <span className="aa-funnel-metric__val">{demo.completed}</span>
              <span className="aa-funnel-metric__label">Completed</span>
            </div>
            <div className="aa-funnel-rate" style={{ '--rate-color': demo.conversion_rate > 30 ? '#10B981' : '#F59E0B' }}>
              {demo.conversion_rate}%
            </div>
          </div>
          <div className="aa-funnel-bar">
            <div className="aa-funnel-bar__fill" style={{ width: `${Math.min(demo.conversion_rate, 100)}%` }} />
          </div>
          <span className="aa-funnel-bar__label">Conversion rate</span>
        </div>

        <div className="aa-funnel-card" data-testid="funnel-audit">
          <div className="aa-funnel-card__header">
            <Eye size={18} />
            <span>AI Audits</span>
          </div>
          <div className="aa-funnel-card__stats">
            <div className="aa-funnel-metric">
              <span className="aa-funnel-metric__val">{audit.started}</span>
              <span className="aa-funnel-metric__label">Started</span>
            </div>
            <div className="aa-funnel-arrow">
              <ArrowUpRight size={16} />
            </div>
            <div className="aa-funnel-metric">
              <span className="aa-funnel-metric__val">{audit.completed}</span>
              <span className="aa-funnel-metric__label">Completed</span>
            </div>
            <div className="aa-funnel-rate" style={{ '--rate-color': audit.conversion_rate > 30 ? '#10B981' : '#F59E0B' }}>
              {audit.conversion_rate}%
            </div>
          </div>
          <div className="aa-funnel-bar">
            <div className="aa-funnel-bar__fill aa-funnel-bar__fill--blue" style={{ width: `${Math.min(audit.conversion_rate, 100)}%` }} />
          </div>
          <span className="aa-funnel-bar__label">Conversion rate</span>
        </div>
      </div>

      {/* Recent activity */}
      <div className="aa-card aa-card--full" data-testid="funnel-recent">
        <h3>Recent Activity</h3>
        <div className="aa-feed">
          {recent.map((item, i) => (
            <div key={i} className="aa-feed__row" style={{ animationDelay: `${i * 0.03}s` }}>
              <span className="aa-feed__dot" data-type={item.type.includes('demo') ? 'page_view' : 'cta_click'} />
              <span className="aa-feed__type" data-type={item.type.includes('demo') ? 'page_view' : 'cta_click'}>
                {item.type.replace(/_/g, ' ')}
              </span>
              <span className="aa-feed__page">{item.website_url}</span>
              <span className="aa-feed__meta">
                {item.name ? `${item.name} (${item.email || ''})` : 'No lead captured'}
              </span>
              <span className="aa-feed__time">{new Date(item.timestamp).toLocaleString()}</span>
            </div>
          ))}
          {recent.length === 0 && <p className="aa-empty">No demos or audits tracked yet</p>}
        </div>
      </div>
    </>
  );
}

/* ============================
   VIEW: Pages (detailed)
   ============================ */
function PagesView({ data }) {
  const { top_pages, browsers, os_list } = data;
  return (
    <>
      <div className="aa-card aa-card--full" data-testid="top-pages-full">
        <h3>All Pages</h3>
        <div className="aa-list aa-list--wide">
          {top_pages.map((p, i) => (
            <div key={i} className="aa-list__item">
              <span className="aa-list__rank">{i + 1}</span>
              <span className="aa-list__name">{p.page}</span>
              <div className="aa-list__bar"><div style={{ width: `${(p.views / (top_pages[0]?.views || 1)) * 100}%` }} /></div>
              <span className="aa-list__val">{p.views}</span>
            </div>
          ))}
          {top_pages.length === 0 && <p className="aa-empty">No pages tracked yet</p>}
        </div>
      </div>

      <div className="aa-row-2">
        <div className="aa-card" data-testid="browsers-chart">
          <h3>Browsers</h3>
          {browsers.length > 0 ? (
            <div className="aa-hbar-list">
              {browsers.map((b, i) => (
                <div key={i} className="aa-hbar">
                  <span className="aa-hbar__label">{b.name}</span>
                  <div className="aa-hbar__track">
                    <div className="aa-hbar__fill" style={{ width: `${(b.value / (browsers[0]?.value || 1)) * 100}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                  <span className="aa-hbar__val">{b.value}</span>
                </div>
              ))}
            </div>
          ) : <p className="aa-empty">No data</p>}
        </div>

        <div className="aa-card" data-testid="os-chart">
          <h3>Operating Systems</h3>
          {os_list.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={os_list} cx="50%" cy="50%" innerRadius={48} outerRadius={66} dataKey="value" paddingAngle={4} stroke="none">
                    {os_list.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="aa-legend">
                {os_list.map((o, i) => (
                  <span key={i} className="aa-legend__item"><span className="aa-legend__dot" style={{ background: COLORS[i % COLORS.length] }} /> {o.name} <strong>{o.value}</strong></span>
                ))}
              </div>
            </>
          ) : <p className="aa-empty">No data</p>}
        </div>
      </div>
    </>
  );
}

/* ============================
   VIEW: Realtime (live visitors + feed)
   ============================ */
function RealtimeView({ data, password }) {
  const { recent_events, summary } = data;
  const [liveData, setLiveData] = useState(null);
  const DEVICE_IC = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const res = await fetch(`${API}/api/analytics/live?password=${encodeURIComponent(password)}`);
        if (res.ok) setLiveData(await res.json());
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Live data fetch failed:', err);
      }
    };
    fetchLive();
    const iv = setInterval(fetchLive, 10000);
    return () => clearInterval(iv);
  }, [password]);

  const formatDuration = (startedAt) => {
    const diff = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
    return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
  };

  return (
    <>
      {/* Live visitors section */}
      <div className="aa-live-banner" data-testid="live-visitors-banner">
        <div className="aa-live-banner__left">
          <div className="aa-live-banner__pulse" />
          <span className="aa-live-banner__count">{liveData?.count ?? '—'}</span>
          <span className="aa-live-banner__text">visitor{liveData?.count !== 1 ? 's' : ''} online now</span>
        </div>
        <span className="aa-live-banner__sub">Updates every 10s</span>
      </div>

      {liveData && liveData.visitors.length > 0 && (
        <div className="aa-card aa-card--full" data-testid="live-visitors-list" style={{ marginBottom: 16 }}>
          <h3>Active Visitors</h3>
          <div className="aa-visitors">
            {liveData.visitors.map((v, i) => {
              const Ic = DEVICE_IC[v.device_type] || Monitor;
              return (
                <div key={i} className="aa-visitor" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="aa-visitor__avatar">
                    <User size={14} />
                  </div>
                  <div className="aa-visitor__info">
                    <span className="aa-visitor__page">{v.page}</span>
                    <span className="aa-visitor__meta">
                      <Ic size={11} /> {v.browser} / {v.os}
                    </span>
                  </div>
                  <div className="aa-visitor__duration">
                    <Clock size={11} />
                    {formatDuration(v.started_at)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event stream */}
      <div className="aa-card aa-card--full" data-testid="recent-events">
        <h3>Activity Stream</h3>
        <div className="aa-feed">
          {recent_events.map((ev, i) => (
            <div key={i} className="aa-feed__row" style={{ animationDelay: `${i * 0.04}s` }}>
              <span className="aa-feed__dot" data-type={ev.event_type} />
              <span className="aa-feed__type" data-type={ev.event_type}>{ev.event_type.replace(/_/g, ' ')}</span>
              <span className="aa-feed__page">{ev.page}</span>
              <span className="aa-feed__meta">{ev.device_type} / {ev.browser}</span>
              <span className="aa-feed__time">{new Date(ev.timestamp).toLocaleTimeString()}</span>
            </div>
          ))}
          {recent_events.length === 0 && <p className="aa-empty">No events yet — visit the site to start tracking</p>}
        </div>
      </div>
    </>
  );
}

/* ============================
   VIEW: Events (breakdown)
   ============================ */
function EventsView({ data }) {
  const { event_types, summary } = data;
  return (
    <>
      <div className="aa-card aa-card--full" data-testid="event-types">
        <h3>Event Breakdown</h3>
        <div className="aa-event-grid">
          {event_types.map((et, i) => (
            <div key={i} className="aa-event-card" style={{ '--ev-color': COLORS[i % COLORS.length] }}>
              <Zap size={18} style={{ color: COLORS[i % COLORS.length] }} />
              <span className="aa-event-card__name">{et.name.replace(/_/g, ' ')}</span>
              <span className="aa-event-card__count">{et.value}</span>
              <span className="aa-event-card__pct">{summary.total_events > 0 ? Math.round((et.value / summary.total_events) * 100) : 0}%</span>
            </div>
          ))}
          {event_types.length === 0 && <p className="aa-empty">No events tracked yet</p>}
        </div>
      </div>
    </>
  );
}

/* ============================
   MAIN COMPONENT
   ============================ */
export default function AdminAnalytics() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('_aa_pw') || '');
  const [data, setData] = useState(null);
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [view, setView] = useState('overview');

  const fetchData = useCallback(async () => {
    if (!password) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/analytics/dashboard?password=${encodeURIComponent(password)}&range=${range}`);
      if (res.status === 403) { setAuthError(true); setPassword(''); sessionStorage.removeItem('_aa_pw'); return; }
      setData(await res.json());
      setAuthError(false);
      setLastRefresh(new Date());
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Analytics fetch failed', err); }
    finally { setLoading(false); }
  }, [password, range]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    if (!password) return;
    const iv = setInterval(fetchData, 30000);
    return () => clearInterval(iv);
  }, [password, fetchData]);

  const handleUnlock = (pw) => { sessionStorage.setItem('_aa_pw', pw); setPassword(pw); setAuthError(false); };

  if (!password || authError) return <PasswordGate onUnlock={handleUnlock} />;
  if (!data) return <div className="aa-loading"><RefreshCw size={18} className="aa-spin" /> Loading...</div>;

  return (
    <div className="aa-shell" data-testid="analytics-dashboard">
      {/* Sidebar */}
      <aside className="aa-sidebar" data-testid="analytics-sidebar">
        <div className="aa-sidebar__brand">
          <div className="aa-sidebar__logo" />
          <span>Analytics</span>
        </div>
        <nav className="aa-sidebar__nav">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              className={`aa-sidebar__btn ${view === item.id ? 'aa-sidebar__btn--on' : ''}`}
              onClick={() => setView(item.id)}
              data-testid={`sidebar-${item.id}`}
            >
              <item.icon size={16} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="aa-sidebar__spacer" />
        <div className="aa-sidebar__foot">
          <span>Skye AI</span>
          <span className="aa-sidebar__ver">v1.0</span>
        </div>
      </aside>

      {/* Content */}
      <main className="aa-main">
        {/* Top bar */}
        <header className="aa-topbar">
          <div className="aa-topbar__left">
            <h1>{NAV_ITEMS.find(n => n.id === view)?.label || 'Overview'}</h1>
            {lastRefresh && <span className="aa-topbar__live"><span className="aa-topbar__dot" /> Live</span>}
          </div>
          <div className="aa-topbar__right">
            <div className="aa-range" data-testid="analytics-range-selector">
              {RANGES.map(r => (
                <button key={r.value} className={range === r.value ? 'aa-range--on' : ''} onClick={() => setRange(r.value)} data-testid={`range-${r.value}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <button className="aa-btn-refresh" onClick={fetchData} disabled={loading} data-testid="refresh-button">
              <RefreshCw size={14} className={loading ? 'aa-spin' : ''} />
            </button>
          </div>
        </header>

        {/* View content */}
        <div className="aa-view" key={view}>
          {view === 'overview' && <OverviewView data={data} range={range} />}
          {view === 'funnels' && <FunnelsView password={password} range={range} />}
          {view === 'pages' && <PagesView data={data} />}
          {view === 'realtime' && <RealtimeView data={data} password={password} />}
          {view === 'events' && <EventsView data={data} />}
        </div>
      </main>
    </div>
  );
}
